import {
  CobaltFormField,
  CobaltFormFieldLabel,
} from '@cae/cobalt-react/components/form-field';
import { CobaltLink } from '@cae/cobalt-react/components/link';
import { CobaltCountryISOEnum, CobaltPhoneNumberInput } from '@cae/cobalt-react/components/phone-number-input';
import { CobaltSectionMessage } from '@cae/cobalt-react/components/section-message';
import { CobaltSidePanel } from '@cae/cobalt-react/components/side-panel';
import { CobaltTextInput } from '@cae/cobalt-react/components/text-input';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { UnsavedChangesModal } from '@shared/components/unsaved-changes-modal/UnsavedChangesModal';
import * as React from 'react';

import type { EditContactPanelProps } from './EditContactPanel.types';

import { useEditContactPanel } from '../../hooks/use-edit-contact-panel/useEditContactPanel';
import { EditContactActionBar } from './edit-contact-action-bar/EditContactActionBar';
import styles from './EditContactPanel.module.css';

const EditContactPanel = React.memo(
  ({ onClose }: Readonly<EditContactPanelProps>): React.ReactElement => {
    const {
      formState,
      isMobile,
      isExitModalOpen,
      isDirty,
      isPending,
      isError,
      isProfileLoading,
      isProfileReady,
      isPhoneValid,
      phoneInputValue,
      phoneInputCountry,
      handlePhoneNumberChange,
      handleSubmit,
      handleCloseAttempt,
      handleExit,
      handleKeepEditing,
    } = useEditContactPanel({ onClose });

    return (
      <>
        <CobaltSidePanel
          isOpen
          showCloseButton
          surtitle="Personal information"
          title="Contact information"
          description="Keep your contact details up to date so we can stay connected."
          onClose={handleCloseAttempt}
          actionBar={
            <EditContactActionBar
              isMobile={isMobile}
              isPending={isPending}
              isProfileReady={isProfileReady}
              isDirty={isDirty}
              isPhoneValid={isPhoneValid}
              onCloseAttempt={handleCloseAttempt}
              onSubmit={handleSubmit}
            />
          }
          data-heap-id="edit-contact-panel"
          size={isMobile ? 'large' : 'small'}
        >
          <div className={styles.form}>
            {(isPending || (isProfileLoading && !isProfileReady)) && <ContentLoader />}

            <CobaltSectionMessage
              variant="info"
              sectionMessageTitle={
                <h3>
                  Certain fields can&apos;t be edited directly. To request a change, you&apos;ll need to{' '}
                  <CobaltLink
                    data-heap-id="profile-contact-support-request-link"
                    href={import.meta.env.VITE_HELP_HOME_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    inline
                    className={styles.inlineLink}
                  >
                    open a support request.
                  </CobaltLink>
                </h3>
              }
              data-heap-id="contact-info-notice"
            />

            {isError && (
              <CobaltSectionMessage
                variant="danger"
                sectionMessageTitle={
                  <h3>We couldn&apos;t save your information. Please try again.</h3>
                }
                data-heap-id="mutation-error-banner"
              />
            )}

            <CobaltFormField id="email">
              <CobaltFormFieldLabel htmlFor="email" label="Email" />
              <CobaltTextInput
                id="email"
                type="email"
                value={formState.email}
                disabled={true}
              />
            </CobaltFormField>

            <CobaltPhoneNumberInput
              key={isProfileReady ? 'ready' : 'loading'}
              id="mobileNumber"
              label="Mobile phone number"
              value={phoneInputValue}
              selectedCountry={phoneInputCountry as CobaltCountryISOEnum}
              onPhoneNumberChange={handlePhoneNumberChange}
              disabled={isPending || (isProfileLoading && !isProfileReady)}
            />
          </div>
        </CobaltSidePanel>

        <UnsavedChangesModal
          isOpen={isExitModalOpen}
          confirmLabel="Keep Editing"
          cancelLabel="Exit"
          onConfirm={handleKeepEditing}
          onCancel={handleExit}
        />
      </>
    );
  }
);

EditContactPanel.displayName = 'EditContactPanel';

export { EditContactPanel };
